import {
  type ColumnFiltersState,
  type FilterFn,
  type Table,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";

import usePagination from "@/components/shared/tables/pagination";
import useTableShortcuts from "@/components/shared/tables/tableShortCuts";
import { useAccountsDataContext } from "./accounts-data-provider";
import { columns } from "./columns";

const AccountsTableInstanceContext = createContext<
  | {
      table: Table<any>;
      setRowSelection: (value: any) => void;
      onGlobalFilterChange: (value: string) => void;
    }
  | undefined
>(undefined);

export function useAccountsTableInstanceContext() {
  const context = useContext(AccountsTableInstanceContext);
  if (context === undefined) {
    throw new Error(
      "useAccountsTableContext must be within AccountsTableInstanceProvider",
    );
  }

  return context;
}

type Props = PropsWithChildren<{}>;

// Enhanced search: types, rules, min/max
const accountsGlobalFilter: FilterFn<any> = (row, columnId, value) => {
  const search = value.toLowerCase();
  const account = row.original;

  // EN → DE labels
  const getAccountTypeLabel = (type: string) => {
    switch (type) {
      case "ASSET":
        return "Anlage";
      case "EXPENSE":
        return "Aufwand";
      case "GWG":
        return "GWG";
      case "REVENUE":
        return "Erlöse";
      default:
        return type;
    }
  };

  // Boolean flags → display text
  const getRuleLabels = (account: any) => {
    const rules = [];
    if (account.buExport === false) rules.push("Kein BU");
    if (account.serviceDateExport === false) rules.push("Kein Datum");
    if (account.faktor2 === true) rules.push("Faktor 2");
    if (account.automatic === true) rules.push("Automatik");
    return rules;
  };

  // Search in account number, name, description, translated account type, rules, and min/max values
  const searchableFields = [
    (account.accountNo || "").replace(/\s/g, ""), // Remove spaces from account number for search
    account.accountName || "",
    account.accountDescription || "",
    getAccountTypeLabel(account.accountType || ""),
    ...getRuleLabels(account),
    account.min?.toString() || "",
    account.max?.toString() || "",
  ];

  return searchableFields.some((field) => field.toLowerCase().includes(search));
};

export const AccountsTableInstanceProvider = ({ children }: Props) => {
  const { accounts, updateAccount, taxCodes } = useAccountsDataContext();
  const [filters, setFilters] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [rowSelection, setRowSelection] = useState({});
  const { pagination, onPaginationChange } = usePagination(200);

  const _columns = useMemo(() => columns(), []);

  const table = useReactTable({
    data: accounts ?? [],
    columns: _columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: accountsGlobalFilter,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: onPaginationChange,
    onGlobalFilterChange: setFilters,
    onColumnFiltersChange: setColumnFilters,
    enableMultiRowSelection: true,
    autoResetPageIndex: false,
    state: {
      rowSelection,
      pagination,
      globalFilter: filters,
      columnFilters,
    },
    initialState: {
      columnVisibility: {
        automatic: false,
      },
    },
  });

  useTableShortcuts({
    handleCmdA: () => table.toggleAllRowsSelected(true),
    handleEsc: () => table.resetRowSelection(true),
  });

  return (
    <AccountsTableInstanceContext.Provider
      value={{
        table,
        setRowSelection,
        onGlobalFilterChange: setFilters,
      }}
    >
      {children}
    </AccountsTableInstanceContext.Provider>
  );
};
